import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import ContactLink from './ContactLink';
import { useFooter } from './hooks';
import Link from './Link';
import { FOOTER_LINK_TYPE } from './constants';
import { LayoutFooterProps } from './types';
import CTA from './CTA';

import { filterProps } from 'utils/helpers';
import { Classname } from 'utils/types';
import { RichText } from 'components/Prismic';

type TFooter = Classname & Omit<LayoutFooterProps, 'autoFooterPadding'>;

const Container = styled.footer`
  ${({ theme: { colors, mq, spacings } }) => css`
    display: grid;
    grid-template-rows: repeat(2, auto);
    grid-auto-flow: column;
    row-gap: 3rem;
    justify-content: space-between;
    background-color: ${colors.backgroundDark};
    padding: 16rem 33rem;

    ${mq.md} {
      grid-template-rows: auto;
      grid-auto-flow: row;
      row-gap: 2.5rem;
      padding: 4rem ${spacings.padding} 2rem;
    }
  `}
`;

const ContactLinks = styled.div`
  ${({ theme: { mq } }) => css`
    display: grid;
    row-gap: 1.5rem;

    ${mq.md} {
      margin-bottom: 3rem;
    }
  `}
`;

const LinksTitle = styled(RichText)`
  ${({ theme: { colors, mq, tp } }) => css`
    display: flex;
    flex-direction: column;

    & > h3 {
      line-height: 1.17;
      color: ${colors.primary};
      font-size: 3rem;

      ${tp.useFont(fonts => fonts.Switzer.bold)};
    }

    ${mq.md} {
      & > h3 {
        line-height: 1.375;
      }
    }
  `}
`;

const Links = styled('div', filterProps('rowCount', 'columnCount'))<{
  rowCount: number;
  columnCount: number;
}>`
  ${({ theme: { mq }, rowCount, columnCount }) => css`
    display: grid;
    grid-template-rows: repeat(${rowCount}, auto);
    grid-template-columns: repeat(${columnCount}, auto);
    row-gap: 1.5rem;
    column-gap: 6rem;
    justify-items: flex-end;
    direction: rtl;

    ${mq.md} {
      grid-template-rows: initial;
      grid-template-columns: auto;
    }
  `}
`;

const StyledLink = styled(Link, filterProps('order'))<{ order: number }>`
  ${({ theme: { mq }, order }) => css`
    order: ${order};

    ${mq.md} {
      order: initial;
    }
  `}
`;

const Footer: React.FC<TFooter> = ({
  className,
  currentLanguage,
  prependFooterLinks,
  footerLinkRows,
}) => {
  const {
    animationTriggerRef,
    animationTriggerElement,
    ctas,
    contactLinks,
    linksTitle,
    links,
    linksRowCount,
    linksColumnCount,
  } = useFooter({
    currentLanguage,
    prependFooterLinks,
    footerLinkRows,
  });

  return (
    <Container className={className}>
      <CTA ctas={ctas} animationTriggerElement={animationTriggerElement} />
      <ContactLinks ref={animationTriggerRef}>
        {contactLinks.map(({ label, address }, index) => (
          <ContactLink key={`${label}${index}`} href={address} prefix="mailto:">
            {label}
          </ContactLink>
        ))}
      </ContactLinks>
      <LinksTitle render={linksTitle} />
      <Links rowCount={linksRowCount} columnCount={linksColumnCount}>
        {links.map(({ label, url, type, target, order }, index) => (
          <StyledLink
            key={`${label}${index}`}
            href={url}
            external={type === FOOTER_LINK_TYPE.EXTERNAL}
            target={target}
            order={order}
          >
            {label}
          </StyledLink>
        ))}
      </Links>
    </Container>
  );
};

export default Footer;
